// Spoken explanation for the guided tour, using the phone's built-in voice (free, no microphone).
// iPhones only allow speech that starts directly inside a tap, so callers speak from the click handler.
type Listener = (speaking: boolean) => void;
const synth = typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null;
let voices: SpeechSynthesisVoice[] = [];
const listeners = new Set<Listener>();
const emit = (v: boolean) => listeners.forEach((l) => l(v));

if (synth) {
  voices = synth.getVoices();
  synth.addEventListener?.('voiceschanged', () => (voices = synth.getVoices()));
}

// Prefer a FEMALE Indian voice in the right language; "Google"/"Natural" voices sound most human.
// Voice lists don't say gender, so we match known female names and skip known male ones.
const FEMALE = /lekha|veena|neerja|kalpana|swara|heera|aditi|raveena|priya|kavya|google (हिन्दी|hindi)|female|samantha|karen|moira|tessa|fiona|serena|zira|susan|hazel|libby|sonia/i;
const MALE = /rishi|hemant|prabhat|madhur|ravi|male|daniel|alex|fred|arthur|oliver|guy|ryan|thomas|aaron/i;

function pick(lang: 'en' | 'hi'): SpeechSynthesisVoice | undefined {
  if (!voices.length && synth) voices = synth.getVoices();
  const want = lang === 'hi' ? ['hi-in', 'hi'] : ['en-in', 'en-gb', 'en-us', 'en'];
  for (const code of want) {
    const inLang = voices.filter((v) => v.lang.replace('_', '-').toLowerCase().startsWith(code));
    const female = inLang.filter((v) => FEMALE.test(v.name) && !/\bmale\b/i.test(v.name.replace(/female/i, '')));
    const notMale = inLang.filter((v) => !MALE.test(v.name.replace(/female/i, '')));
    const best = female.find((v) => /natural|neural|google|premium|enhanced/i.test(v.name)) ?? female[0] ?? notMale[0];
    if (best) return best;
  }
  return undefined;
}

// Natural-voice recordings (Sarvam), if they have been made: /tour-audio/manifest.json lists them.
let recorded: Record<string, string> = {};
let audio: HTMLAudioElement | null = null;
// no-cache: always check for the latest list, or a phone keeps an old one and new steps fall back to the robot voice.
fetch('/tour-audio/manifest.json', { cache: 'no-cache' })
  .then((r) => (r.ok ? r.json() : null))
  .then((m) => {
    if (m?.files) recorded = m.files;
  })
  .catch(() => undefined);

export const voiceAvailable = () => !!synth || Object.keys(recorded).length > 0;

/** Plays the recording for this step if there is one; otherwise uses the phone's voice. */
export function speakStep(file: string, text: string, lang: 'en' | 'hi') {
  stopSpeaking();
  const name = `${lang}/${file}`;
  if (recorded[name]) {
    audio = new Audio(`/tour-audio/${name}.mp3?v=${recorded[name]}`);
    audio.onplay = () => emit(true);
    audio.onended = () => emit(false);
    audio.onpause = () => emit(false);
    audio.play().catch(() => speak(text, lang));
    return;
  }
  speak(text, lang);
}

export function onSpeaking(l: Listener) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function speak(text: string, lang: 'en' | 'hi') {
  if (!synth) return;
  // Chrome sometimes stays silent if speech is cancelled and restarted in the same instant,
  // so when something is already playing, stop it and start the new step a moment later.
  // When nothing is playing, start at once (iPhone/Safari need speech to begin inside the tap).
  const busy = synth.speaking || synth.pending;
  if (busy) {
    synth.cancel();
    window.setTimeout(() => say(text, lang), 120);
  } else {
    say(text, lang);
  }
}

function say(text: string, lang: 'en' | 'hi') {
  if (!synth) return;
  const u = new SpeechSynthesisUtterance(text);
  const v = pick(lang);
  if (v) u.voice = v;
  u.lang = v?.lang ?? (lang === 'hi' ? 'hi-IN' : 'en-IN');
  u.rate = lang === 'hi' ? 0.95 : 1;
  u.pitch = 1.1; // slightly higher, warmer
  u.onstart = () => emit(true);
  u.onend = () => emit(false);
  u.onerror = () => emit(false);
  synth.speak(u);
  // Some browsers pause long speech; nudging keeps it going.
  synth.resume();
}

export function stopSpeaking() {
  if (audio) {
    audio.pause();
    audio = null;
  }
  synth?.cancel();
  emit(false);
}
